import React, { useRef, useState } from 'react';

/**
 * MagneticButton Component
 * Creates a button that attracts towards the cursor with a magnetic effect
 */
const MagneticButton = ({
  children,
  className = '',
  strength = 0.4,
  radius = 200,
  as: Component = 'button',
  onClick,
  href,
  ...props
}) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const distanceX = e.clientX - centerX;
    const distanceY = e.clientY - centerY;

    // Calculate distance from center
    const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

    if (distance < radius) {
      // Apply magnetic effect based on distance
      const magnetStrength = (1 - distance / radius) * strength;
      setPosition({
        x: distanceX * magnetStrength,
        y: distanceY * magnetStrength,
      });
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setPosition({ x: 0, y: 0 });
  };

  const buttonProps = {
    ref: buttonRef,
    className: `magnetic-button relative inline-block ${className}`,
    onMouseMove: handleMouseMove,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick,
    ...(Component === 'a' ? { href } : {}),
    ...props,
  };

  return (
    <Component {...buttonProps}>
      <span
        className="magnetic-content block transition-transform duration-300 ease-out"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
      >
        {children}
      </span>
      
      {/* Magnetic glow effect */}
      <span
        className={`absolute inset-0 transition-opacity duration-300 pointer-events-none ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'radial-gradient(circle at center, rgba(0, 255, 209, 0.15) 0%, transparent 70%)',
          transform: `translate(${position.x * 0.5}px, ${position.y * 0.5}px)`,
          filter: 'blur(20px)',
        }}
      />
    </Component>
  );
};

/**
 * MagneticLink Component
 * Magnetic effect for text links
 */
const MagneticLink = ({
  children,
  className = '',
  strength = 0.3,
  href = '#',
  onClick,
  ...props
}) => {
  const linkRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!linkRef.current) return;

    const rect = linkRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const distanceX = e.clientX - centerX;
    const distanceY = e.clientY - centerY;

    setPosition({
      x: distanceX * strength,
      y: distanceY * strength,
    });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <a
      ref={linkRef}
      href={href}
      className={`magnetic-link inline-block ${className}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      {...props}
    >
      <span
        className="block transition-transform duration-200 ease-out"
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
      >
        {children}
      </span>
    </a>
  );
};

/**
 * MagneticIconButton Component
 * Circular magnetic button for icons
 */
const MagneticIconButton = ({
  children,
  className = '',
  strength = 0.5,
  onClick,
  ...props
}) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);

  const handleMouseMove = (e) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const distanceX = e.clientX - centerX;
    const distanceY = e.clientY - centerY;

    setPosition({
      x: distanceX * strength,
      y: distanceY * strength,
    });
  };

  const handleMouseEnter = () => {
    setScale(1.1);
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
    setScale(1);
  };

  return (
    <button
      ref={buttonRef}
      className={`magnetic-icon-button relative ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      {...props}
    >
      <span
        className="block transition-all duration-300 ease-out"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
        }}
      >
        {children}
      </span>
    </button>
  );
};

/**
 * MagneticCard Component
 * Card with 3D tilt effect following cursor
 */
const MagneticCard = ({
  children,
  className = '',
  tiltStrength = 10,
  glareEnabled = true,
  ...props
}) => {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [glarePosition, setGlarePosition] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const percentX = (e.clientX - centerX) / (rect.width / 2);
    const percentY = (e.clientY - centerY) / (rect.height / 2);

    setTransform({
      rotateX: -percentY * tiltStrength,
      rotateY: percentX * tiltStrength,
    });

    // Update glare position
    const glareX = ((e.clientX - rect.left) / rect.width) * 100;
    const glareY = ((e.clientY - rect.top) / rect.height) * 100;
    setGlarePosition({ x: glareX, y: glareY });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTransform({ rotateX: 0, rotateY: 0 });
    setGlarePosition({ x: 50, y: 50 });
  };

  return (
    <div
      ref={cardRef}
      className={`magnetic-card relative ${className}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg)`,
        transition: isHovered ? 'transform 0.1s ease-out' : 'transform 0.5s ease-out',
        transformStyle: 'preserve-3d',
      }}
      {...props}
    >
      {children}
      
      {/* Glare effect */}
      {glareEnabled && (
        <div
          className={`absolute inset-0 pointer-events-none transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            background: `radial-gradient(circle at ${glarePosition.x}% ${glarePosition.y}%, rgba(255,255,255,0.15) 0%, transparent 60%)`,
          }}
        />
      )}
    </div>
  );
};

export { MagneticButton, MagneticLink, MagneticIconButton, MagneticCard };
